// src/utils/Logger.ts
import { Capacitor } from '@capacitor/core';

export interface LogEntry {
  timestamp: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  details?: any;
}

type LogCallback = (entry: LogEntry) => void;

class Logger {
  private static logs: LogEntry[] = [];
  private static subscribers: LogCallback[] = [];

  private static addLog(level: LogEntry['level'], message: string, details?: any) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      details,
    };

    this.logs.push(entry);
    // Keep only last 100 logs
    if (this.logs.length > 100) {
      this.logs.shift();
    }

    // Notify subscribers
    this.subscribers.forEach((callback) => callback(entry));

    if (Capacitor.isNativePlatform()) {
      switch (level) {
        case 'debug':
          console.debug(`[DEBUG] ${message}`, details);
          break;
        case 'info':
          console.info(`[INFO] ${message}`, details);
          break;
        case 'warn':
          console.warn(`[WARN] ${message}`, details);
          break;
        case 'error':
          console.error(`[ERROR] ${message}`, details);
          break;
      }
    }
  }

  static debug(message: string, details?: any) {
    this.addLog('debug', message, details);
  }

  static info(message: string, details?: any) {
    this.addLog('info', message, details);
  }

  static warn(message: string, details?: any) {
    this.addLog('warn', message, details);
  }

  static error(message: string, error?: any) {
    this.addLog('error', message, error);
    if (error?.stack) {
      this.addLog('error', 'Stack trace:', error.stack);
    }
  }

  static logObject(label: string, obj: any) {
    this.addLog('debug', label, obj);
  }

  static getLogs(): LogEntry[] {
    return this.logs;
  }

  static subscribe(callback: LogCallback) {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter((cb) => cb !== callback);
    };
  }

  static clear() {
    this.logs = [];
    this.subscribers.forEach((callback) =>
      callback({
        timestamp: new Date().toISOString(),
        level: 'info',
        message: 'Logs cleared',
      }),
    );
  }
}

// eslint-disable-next-line import/no-default-export
export default Logger;
